import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Upload, ZoomIn, ZoomOut, RotateCcw, X, Move, Crosshair, Lock, Unlock } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const FloorPlanCanvas = forwardRef(({
  onImageLoad,
  showCenterLines = false,
  canvasRef,
  floorPlanRotation = 0,
  onZoomChange,
  onLockChange,
  onOffsetChange,
  centerBox = null,
  confirmedBox = null,
  isDrawingCenterBox = false,
  onCenterBoxChange,
  onDrawingComplete,
}, ref) => {
  const [image, setImage] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLocked, setIsLocked] = useState(false);
  
  // Local state for drawing the center box
  const [drawingStart, setDrawingStart] = useState(null);
  const [tempBox, setTempBox] = useState(null);
  
  // Refs to access latest state in event handlers
  const tempBoxRef = useRef(null);
  const drawingStartRef = useRef(null);
  const lastOffsetRef = useRef({ x: 0, y: 0 });
  
  // Keep refs in sync with state
  useEffect(() => {
    tempBoxRef.current = tempBox;
  }, [tempBox]);
  
  useEffect(() => {
    drawingStartRef.current = drawingStart;
  }, [drawingStart]);
  
  useEffect(() => {
    lastOffsetRef.current = offset;
  }, [offset]);
  
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Notify parent of zoom changes
  useEffect(() => {
    onZoomChange?.(Math.round(scale * 100));
  }, [scale, onZoomChange]);

  // Notify parent of offset changes (for syncing locked markers)
  useEffect(() => {
    onOffsetChange?.(offset);
  }, [offset, onOffsetChange]);

  // Notify parent of lock changes
  useEffect(() => {
    onLockChange?.(isLocked);
  }, [isLocked, onLockChange]);

  // Toggle lock
  const toggleLock = useCallback(() => {
    setIsLocked(prev => {
      const newState = !prev;
      toast.success(newState ? 'Floor plan locked!' : 'Floor plan unlocked!');
      return newState;
    });
  }, []);

  // Center the floor plan in the canvas
  const handleCenterFloorPlan = useCallback(() => {
    if (isLocked) return;
    if (!containerRef.current || !image) return;
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    
    setOffset({
      x: (containerWidth - imageSize.width * scale) / 2,
      y: (containerHeight - imageSize.height * scale) / 2,
    });
    
    toast.success('Floor plan centered!');
  }, [image, imageSize, scale, isLocked]);

  // Handle file upload
  const handleFileUpload = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage(e.target.result);
        setImageSize({ width: img.width, height: img.height });
        
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          const scaleX = (containerWidth * 0.85) / img.width;
          const scaleY = (containerHeight * 0.85) / img.height;
          const initialScale = Math.min(scaleX, scaleY, 1);
          setScale(initialScale);
          
          setOffset({
            x: (containerWidth - img.width * initialScale) / 2,
            y: (containerHeight - img.height * initialScale) / 2,
          });
        }
        
        onImageLoad?.({ width: img.width, height: img.height });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, [onImageLoad]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, [handleFileUpload]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  }, [handleFileUpload]);

  // Convert screen coordinates to image-relative coordinates
  const screenToImageCoords = useCallback((clientX, clientY) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    // Get position relative to container
    const containerX = clientX - rect.left;
    const containerY = clientY - rect.top;
    // Convert to image coordinates (accounting for offset and scale)
    const imageX = (containerX - offset.x) / scale;
    const imageY = (containerY - offset.y) / scale;
    return { x: imageX, y: imageY };
  }, [offset, scale]);

  // Transform screen delta to canvas delta (accounting for floor plan rotation)
  const screenDeltaToCanvas = useCallback((dx, dy) => {
    if (!floorPlanRotation) return { x: dx, y: dy };
    const rad = (-floorPlanRotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
  }, [floorPlanRotation]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    
    // Handle center box drawing mode
    if (isDrawingCenterBox && image) {
      e.preventDefault();
      e.stopPropagation();
      const coords = screenToImageCoords(e.clientX, e.clientY);
      setDrawingStart(coords);
      setTempBox(null);
      return;
    }
    
    if (isLocked) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y });
  }, [offset, isLocked, isDrawingCenterBox, image, screenToImageCoords]);

  const handleMouseMove = useCallback((e) => {
    // Handle center box drawing
    if (isDrawingCenterBox && drawingStart) {
      const coords = screenToImageCoords(e.clientX, e.clientY);
      const x = Math.min(drawingStart.x, coords.x);
      const y = Math.min(drawingStart.y, coords.y);
      const width = Math.abs(coords.x - drawingStart.x);
      const height = Math.abs(coords.y - drawingStart.y);
      setTempBox({ x, y, width, height });
      return;
    }
    
    if (!isDragging || isLocked) return;
    const screenDx = e.clientX - dragStart.x;
    const screenDy = e.clientY - dragStart.y;
    const { x: canvasDx, y: canvasDy } = screenDeltaToCanvas(screenDx, screenDy);
    const newOffset = {
      x: dragStart.ox + canvasDx,
      y: dragStart.oy + canvasDy,
    };
    setOffset(newOffset);
  }, [isDragging, dragStart, isLocked, isDrawingCenterBox, drawingStart, screenToImageCoords, screenDeltaToCanvas]);

  const handleMouseUp = useCallback(() => {
    // Complete center box drawing using refs to get latest state
    const currentTempBox = tempBoxRef.current;
    const currentDrawingStart = drawingStartRef.current;
    
    if (isDrawingCenterBox && currentDrawingStart && currentTempBox && currentTempBox.width > 20 && currentTempBox.height > 20) {
      onCenterBoxChange?.(currentTempBox);
      onDrawingComplete?.();
      setTempBox(null);
      setDrawingStart(null);
    } else if (isDrawingCenterBox && currentDrawingStart) {
      // Box too small or incomplete - just clear
      setTempBox(null);
      setDrawingStart(null);
      onDrawingComplete?.();  // Also complete drawing mode
    }
    
    setIsDragging(false);
  }, [isDrawingCenterBox, onCenterBoxChange, onDrawingComplete]);

  // Handle wheel zoom - disabled when locked
  const handleWheel = useCallback((e) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => Math.min(Math.max(prev * delta, 0.1), 5));
  }, [isLocked]);

  // Prevent pinch-to-zoom and wheel zoom when locked (for MacBook trackpad)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preventZoom = (e) => {
      if (isLocked) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Prevent wheel zoom when locked
    container.addEventListener('wheel', preventZoom, { passive: false });
    
    // Prevent pinch-to-zoom gesture when locked
    container.addEventListener('gesturestart', preventZoom, { passive: false });
    container.addEventListener('gesturechange', preventZoom, { passive: false });
    container.addEventListener('gestureend', preventZoom, { passive: false });

    return () => {
      container.removeEventListener('wheel', preventZoom);
      container.removeEventListener('gesturestart', preventZoom);
      container.removeEventListener('gesturechange', preventZoom);
      container.removeEventListener('gestureend', preventZoom);
    };
  }, [isLocked]);

  const zoomIn = useCallback(() => {
    if (isLocked) return;
    setScale((prev) => Math.min(prev * 1.2, 5));
  }, [isLocked]);

  const zoomOut = useCallback(() => {
    if (isLocked) return;
    setScale((prev) => Math.max(prev * 0.8, 0.1));
  }, [isLocked]);

  const resetView = useCallback(() => {
    if (isLocked) return;
    if (containerRef.current && imageSize.width && imageSize.height) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const scaleX = (containerWidth * 0.85) / imageSize.width;
      const scaleY = (containerHeight * 0.85) / imageSize.height;
      const initialScale = Math.min(scaleX, scaleY, 1);
      setScale(initialScale);
      setOffset({
        x: (containerWidth - imageSize.width * initialScale) / 2,
        y: (containerHeight - imageSize.height * initialScale) / 2,
      });
    }
  }, [imageSize, isLocked]);

  const clearImage = useCallback(() => {
    if (isLocked) return;
    setImage(null);
    setImageSize({ width: 0, height: 0 });
    setScale(1);
    setOffset({ x: 0, y: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Notify parent that image was cleared
    onImageLoad?.(null);
  }, [isLocked, onImageLoad]);

  // Load image from base64 data
  const loadImage = useCallback((imageData) => {
    if (!imageData) return;
    const img = new Image();
    img.onload = () => {
      setImage(imageData);
      setImageSize({ width: img.width, height: img.height });
      // Fit to container
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        const scaleX = (containerWidth * 0.85) / img.width;
        const scaleY = (containerHeight * 0.85) / img.height;
        const initialScale = Math.min(scaleX, scaleY, 1);
        setScale(initialScale);
        setOffset({
          x: (containerWidth - img.width * initialScale) / 2,
          y: (containerHeight - img.height * initialScale) / 2,
        });
      }
    };
    img.src = imageData;
  }, []);

  // Expose functions to parent via ref
  useImperativeHandle(ref, () => ({
    zoomIn,
    zoomOut,
    isLocked,
    scale,
    resetView,
    toggleLock,
    handleCenterFloorPlan,
    clearImage,
    getImage: () => image,
    loadImage,
    getTransform: () => ({ scale, offset, imageSize }),
  }), [zoomIn, zoomOut, isLocked, scale, offset, imageSize, resetView, toggleLock, handleCenterFloorPlan, clearImage, image, loadImage]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  return (
    <div 
      ref={(el) => {
        containerRef.current = el;
        if (canvasRef) canvasRef.current = el;
      }}
      className="floor-plan-canvas"
      style={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        cursor: isDrawingCenterBox ? 'crosshair' : 'default',
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onWheel={handleWheel}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      data-testid="floor-plan-canvas"
    >
      {!image ? (
        <div 
          className="upload-zone"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-zone"
        >
          <div 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '48px',
              borderRadius: '12px',
              transition: 'background-color 0.2s',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Upload size={28} style={{ color: '#2563eb' }} />
            </div>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1f2937',
              marginBottom: '8px',
            }}>
              Upload Floor Plan
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              marginBottom: '4px',
            }}>
              Drag and drop or click to upload
            </p>
            <p style={{ 
              fontSize: '12px', 
              color: '#9ca3af',
            }}>
              Supports PNG, JPG, JPEG
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
            data-testid="floor-plan-input"
          />
        </div>
      ) : (
        <>
          <div
            style={{
              position: 'absolute',
              left: offset.x,
              top: offset.y,
              width: imageSize.width * scale,
              height: imageSize.height * scale,
              cursor: isDrawingCenterBox ? 'crosshair' : (isLocked ? 'not-allowed' : 'move'),
              transform: `rotate(${floorPlanRotation}deg)`,
              transformOrigin: confirmedBox 
                ? `${confirmedBox.centerX * scale}px ${confirmedBox.centerY * scale}px`
                : 'center center',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            data-testid="floor-plan-image-container"
          >
            <img
              src={image}
              alt="Floor Plan"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
              draggable={false}
            />

            {/* Center lines - use confirmed box corners if available, otherwise use floor plan center */}
            {showCenterLines && !centerBox && imageSize.width > 0 && imageSize.height > 0 && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
                preserveAspectRatio="none"
              >
                {confirmedBox ? (
                  <>
                    {/* Blue dashed X from confirmed box corners (no red outline) */}
                    <line
                      x1={confirmedBox.x}
                      y1={confirmedBox.y}
                      x2={confirmedBox.x + confirmedBox.width}
                      y2={confirmedBox.y + confirmedBox.height}
                      stroke="#2563eb"
                      strokeWidth={2}
                      strokeDasharray="8,4"
                      opacity="0.8"
                    />
                    <line
                      x1={confirmedBox.x + confirmedBox.width}
                      y1={confirmedBox.y}
                      x2={confirmedBox.x}
                      y2={confirmedBox.y + confirmedBox.height}
                      stroke="#2563eb"
                      strokeWidth={2}
                      strokeDasharray="8,4"
                      opacity="0.8"
                    />
                    {/* Center point marker at the box center */}
                    <circle
                      cx={confirmedBox.centerX}
                      cy={confirmedBox.centerY}
                      r={12}
                      fill="#dc2626"
                      stroke="#ffffff"
                      strokeWidth={3}
                    />
                    <circle
                      cx={confirmedBox.centerX}
                      cy={confirmedBox.centerY}
                      r={5}
                      fill="#ffffff"
                    />
                    {/* Label */}
                    <text
                      x={confirmedBox.centerX}
                      y={confirmedBox.centerY - 20}
                      textAnchor="middle"
                      fill="#dc2626"
                      fontSize={14}
                      fontWeight="bold"
                      style={{ textShadow: '0 0 4px white, 0 0 4px white' }}
                    >
                      CENTER
                    </text>
                  </>
                ) : (
                  <>
                    {/* Default diagonal lines from floor plan corners - big X */}
                    <line
                      x1={0}
                      y1={0}
                      x2={imageSize.width}
                      y2={imageSize.height}
                      stroke="#2563eb"
                      strokeWidth={2}
                      strokeDasharray="8,4"
                      opacity="0.6"
                    />
                    <line
                      x1={imageSize.width}
                      y1={0}
                      x2={0}
                      y2={imageSize.height}
                      stroke="#2563eb"
                      strokeWidth={2}
                      strokeDasharray="8,4"
                      opacity="0.6"
                    />
                    <circle
                      cx={imageSize.width / 2}
                      cy={imageSize.height / 2}
                      r={8}
                      fill="#2563eb"
                      stroke="#ffffff"
                      strokeWidth={2}
                    />
                  </>
                )}
              </svg>
            )}

            {/* User-drawn Center Box */}
            {(centerBox || tempBox) && (
              <svg
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  overflow: 'visible',
                }}
                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
                preserveAspectRatio="none"
              >
                {/* Use tempBox while drawing, or centerBox when finalized */}
                {(() => {
                  const box = tempBox || centerBox;
                  if (!box) return null;
                  return (
                    <>
                      {/* The box outline */}
                      <rect
                        x={box.x}
                        y={box.y}
                        width={box.width}
                        height={box.height}
                        fill="rgba(220, 38, 38, 0.1)"
                        stroke="#dc2626"
                        strokeWidth={2}
                        strokeDasharray="8,4"
                      />
                      {/* Diagonal lines within the box */}
                      <line
                        x1={box.x}
                        y1={box.y}
                        x2={box.x + box.width}
                        y2={box.y + box.height}
                        stroke="#2563eb"
                        strokeWidth={2}
                        strokeDasharray="6,3"
                        opacity="0.8"
                      />
                      <line
                        x1={box.x + box.width}
                        y1={box.y}
                        x2={box.x}
                        y2={box.y + box.height}
                        stroke="#2563eb"
                        strokeWidth={2}
                        strokeDasharray="6,3"
                        opacity="0.8"
                      />
                      {/* Center point */}
                      <circle
                        cx={box.x + box.width / 2}
                        cy={box.y + box.height / 2}
                        r={10}
                        fill="#dc2626"
                        stroke="#ffffff"
                        strokeWidth={3}
                      />
                      {/* Center point inner dot */}
                      <circle
                        cx={box.x + box.width / 2}
                        cy={box.y + box.height / 2}
                        r={4}
                        fill="#ffffff"
                      />
                      {/* Label showing "CENTER" */}
                      <text
                        x={box.x + box.width / 2}
                        y={box.y + box.height / 2 - 20}
                        textAnchor="middle"
                        fill="#dc2626"
                        fontSize={14}
                        fontWeight="bold"
                        style={{ textShadow: '0 0 3px white, 0 0 3px white' }}
                      >
                        CENTER
                      </text>
                    </>
                  );
                })()}
              </svg>
            )}
          </div>
        </>
      )}
    </div>
  );
});

FloorPlanCanvas.displayName = 'FloorPlanCanvas';

export default FloorPlanCanvas;
